import { Container, Graphics } from 'pixi.js';
import { DIAMOND, TilePinnedOverlay, type Transient } from './tilePinnedOverlay';
import { abilityFootprintCells, type FootprintAnchor } from './abilityFootprint';
import type { AbilityShape } from '../sim/combat/abilityCatalog';

/**
 * The **announced-area overlay** for monster abilities (PRD #793): the exact
 * footprint of a telegraphed cast, drawn on the ground for the whole warning and
 * gone the instant the damage lands.
 *
 * It is the presentation half of the kernel's telegraph law. The law says a
 * hostile area must be announced before anything happens in it; the announcement
 * is only worth something if what is drawn is what will be selected, so this
 * derives its cells from the same declared shape resolution reads
 * ({@link abilityFootprintCells}) rather than from a hand-drawn marker. Retuning
 * a Geyser's radius moves this drawing with it.
 *
 * Two properties are deliberate.
 *
 * **It holds, then flashes.** The area is fully readable from the first frame,
 * because a warning that fades in has spent part of its own lead being hard to
 * see, and it brightens as the impact tick approaches so the moment to be gone
 * is legible without counting.
 *
 * **It is one graphic per announcement.** A pattern that announces five
 * footprints at once produces five, each with its own lifetime, which is what
 * lets a Meteor Shower's later impacts stay on screen while its first is already
 * over.
 *
 * A {@link TilePinnedOverlay} like the storm's spawn sparkle: a child of the
 * camera-transformed world container, aged from the render clock, self-dismissing.
 */

/** Hostile red-orange: the one colour the game uses for "do not be here". */
const AREA_COLOR = 0xff5a3c;
const EDGE_COLOR = 0xffd0a0;

/** Fallback lifetime when an announcement carries no usable countdown (50ms ticks). */
const DEFAULT_LEAD_TICKS = 20;
const TICK_MS = 50;

/** Alpha at the start of the warning, and at the moment of impact. */
const OPENING_ALPHA = 0.22;
const IMPACT_ALPHA = 0.55;

/** Pulses per second while the warning runs, so the area reads as live. */
const PULSE_HZ = 2;

interface Announcement extends Transient {
  fill: Graphics;
}

export class DungeonTelegraphOverlay extends TilePinnedOverlay<Announcement> {
  /**
   * Draw one announced area. `leadTicks` is the warning the server declared, so
   * the drawing disappears exactly as the damage lands.
   */
  announce(
    shape: AbilityShape,
    anchor: FootprintAnchor,
    leadTicks = DEFAULT_LEAD_TICKS,
  ): void {
    const cells = abilityFootprintCells(shape, anchor);
    if (cells.length === 0) return;
    const container = new Container();
    const fill = new Graphics();
    for (const cell of cells) {
      const centre = this.centre(cell);
      fill
        .poly(DIAMOND.map((value, index) => value + (index % 2 === 0 ? centre.x : centre.y)))
        .fill({ color: AREA_COLOR, alpha: 0.5 })
        .stroke({ width: 1, color: EDGE_COLOR, alpha: 0.5 });
    }
    container.addChild(fill);
    this.addChild(container);
    const ttlMs = Math.max(TICK_MS, (leadTicks || DEFAULT_LEAD_TICKS) * TICK_MS);
    this.addTransient({ gfx: container, fill, ageMs: 0, ttlMs });
  }

  /** How many announcements are currently drawn. Used by the renderer's idle check. */
  get activeCount(): number {
    return this.transients.length;
  }

  /** Age every announcement by one frame; a static refresh holds steady. */
  update(dtMs: number): void {
    if (dtMs <= 0) return;
    this.ageTransients(dtMs, (announcement, life) => {
      const pulse =
        0.85 + 0.15 * Math.sin((announcement.ageMs / 1000) * PULSE_HZ * Math.PI * 2);
      announcement.fill.alpha =
        (OPENING_ALPHA + (IMPACT_ALPHA - OPENING_ALPHA) * life) * pulse;
    });
  }
}
